import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  User, Users, Notebook, Lock, Unlock, Trash2, Plus, Search, MessageSquare,
  FileText, GitPullRequest, Sparkles, ArrowRight, Tag as TagIcon, Hash,
  FileDown, FileJson, Loader2, Plane, CalendarClock, Eye,
} from "lucide-react";
import { toast } from "sonner";
import HandoverPreviewDrawer from "@/components/HandoverPreviewDrawer";

const KIND_META = {
  activity: { icon: Sparkles, color: "text-sky-400/90", label: "Activity" },
  note:     { icon: Notebook, color: "text-emerald-400/90", label: "Note" },
  decision: { icon: GitPullRequest, color: "text-amber-400/90", label: "Decision" },
  process:  { icon: FileText, color: "text-fuchsia-400/90", label: "Process" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export default function Memory() {
  const { user, isAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const [people, setPeople] = useState([]);
  const [entries, setEntries] = useState([]);
  const [selectedId, setSelectedId] = useState(params.get("person") || "");
  const [loadingPeople, setLoadingPeople] = useState(true);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [showAlias, setShowAlias] = useState(false);
  const [note, setNote] = useState({ kind: "note", title: "", content: "", tags: "", private: false });
  const [aliasStr, setAliasStr] = useState("");
  const [saving, setSaving] = useState(false);
  const [pack, setPack] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [togglingDeparture, setTogglingDeparture] = useState(false);
  const [showHandoverPreview, setShowHandoverPreview] = useState(false);

  const loadPeople = async () => {
    try {
      const { data } = await api.get("/memory/people");
      setPeople(data);
      if (!selectedId && data.length) {
        // Default to the caller's own memory.
        const me = data.find((p) => p.user_id === user?.id);
        setSelectedId((me || data[0]).user_id);
      }
    } finally { setLoadingPeople(false); }
  };
  const loadEntries = async () => {
    if (!selectedId) return;
    setLoadingEntries(true);
    try {
      const { data } = await api.get("/memory", {
        params: { subject_user_id: selectedId, q: q || undefined, limit: 200 },
      });
      setEntries(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load memory");
    } finally { setLoadingEntries(false); }
  };
  useEffect(() => { loadPeople(); }, []); // eslint-disable-line
  useEffect(() => { setPack(null); loadEntries(); setParams((p) => { const n = new URLSearchParams(p); if (selectedId) n.set("person", selectedId); return n; }, { replace: true }); }, [selectedId, q]); // eslint-disable-line

  // Auto-open the Add dialog when arriving from a nudge CTA and pre-fill a
  // friendly Friday-recap title so users know exactly what to write.
  useEffect(() => {
    const nudgeId = params.get("nudge");
    if (!nudgeId || !user) return;
    const templates = {
      friday_recap: {
        kind: "note",
        title: "Friday recap — what shipped this week",
        content: "• Wins:\n• Blockers:\n• What's next Monday:",
        tags: "friday-recap,weekly",
      },
      weekend_load: {
        kind: "process",
        title: "Async playbook — weekend-safe answers",
        content: "Steps I'd want a teammate to try before pinging me on a weekend:\n1. \n2. \n3. ",
        tags: "async,playbook",
      },
      async_docs: {
        kind: "process",
        title: "Async-first FAQ for common after-hours questions",
        content: "Q: \nA: \n\nQ: \nA: ",
        tags: "async,docs",
      },
    };
    const cleanId = nudgeId.startsWith("team_") ? nudgeId.slice(5) : nudgeId;
    const seed = templates[cleanId];
    if (seed) {
      setNote((prev) => ({ ...prev, ...seed, private: false }));
      setShowAdd(true);
    }
  }, [params.get("nudge"), user?.id]); // eslint-disable-line

  const selectedPerson = useMemo(
    () => people.find((p) => p.user_id === selectedId),
    [people, selectedId],
  );
  const canEditSubject = selectedId === user?.id || isAdmin;
  const isSelf = selectedId === user?.id;

  const submitNote = async () => {
    if (!note.title.trim() || !note.content.trim() || saving) {
      toast.error("Title and content are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        kind: note.kind,
        title: note.title.trim(),
        content: note.content,
        tags: note.tags.split(",").map((t) => t.trim()).filter(Boolean),
        private: note.private,
      };
      if (!isSelf && isAdmin) payload.subject_user_id = selectedId;
      await api.post("/memory", payload);
      // Celebrate if the user arrived here from a nudge CTA.
      const nudgeId = params.get("nudge");
      const nudgeScope = params.get("scope") || "personal";
      if (nudgeId) {
        try {
          await api.post("/bot-access/nudges/action-completed",
                         { nudge_id: nudgeId, scope: nudgeScope });
          // Also sync local dismissal so BotAccess UI doesn't re-flash it.
          try {
            const prev = JSON.parse(localStorage.getItem("ragnes_dismissed_nudges") || "[]");
            const displayId = nudgeScope === "team" ? `team_${nudgeId}` : nudgeId;
            if (!prev.includes(displayId)) {
              prev.push(displayId);
              localStorage.setItem("ragnes_dismissed_nudges", JSON.stringify(prev));
            }
          } catch { /* non-fatal */ }
        } catch { /* non-fatal */ }
        toast.success("🎉 Nice — that nudge is done. Ragnes will remember you handled it.");
        // Clear the query params so refresh doesn't re-celebrate.
        setParams({});
      } else {
        toast.success("Added to memory");
      }
      setShowAdd(false);
      setNote({ kind: "note", title: "", content: "", tags: "", private: false });
      loadEntries();
      loadPeople();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add entry");
    } finally { setSaving(false); }
  };

  const removeEntry = async (id) => {
    if (!window.confirm("Delete this memory entry?")) return;
    try {
      await api.delete(`/memory/${id}`);
      toast.success("Deleted");
      loadEntries();
      loadPeople();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const togglePrivate = async (e) => {
    try {
      await api.patch(`/memory/${e.id}`, {
        kind: e.kind, title: e.title, content: e.content,
        tags: e.tags || [], private: !e.private,
      });
      loadEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to toggle");
    }
  };

  const saveAliases = async () => {
    try {
      const clean = aliasStr.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
      const url = isSelf ? "/memory/aliases" : `/memory/aliases?user_id=${selectedId}`;
      await api.post(url, { aliases: clean });
      toast.success("Aliases saved");
      setShowAlias(false);
      loadPeople();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save aliases");
    }
  };

  const generateHandover = async () => {
    if (!selectedPerson || generating) return;
    setGenerating(true);
    setPack(null);
    try {
      // POST now returns 202 + job_id — poll until done.
      const { data } = await api.post(`/memory/handover/${selectedId}`);
      const jobId = data.job_id;
      const deadline = Date.now() + 180_000;
      let job = null;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        const res = await api.get(`/memory/handover/jobs/${jobId}`);
        job = res.data;
        if (job.status !== "pending" && job.status !== "running") break;
      }
      if (!job || job.status !== "done") {
        throw new Error(job?.error || "Generation timed out");
      }
      // Fetch the full payload view via a light reconstruction — the job row
      // carries preview fields; downloads go through pack_id.
      setPack({
        pack_id: job.pack_id,
        payload: {
          employee: { name: job.employee_name || selectedPerson.name },
          counts: job.counts || {},
          coverage_score: job.coverage_score ?? 0,
          narrative: job.narrative || "",
        },
      });
      toast.success("Handover pack ready");
      loadPeople();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Failed to generate handover pack");
    } finally { setGenerating(false); }
  };

  const downloadPack = async (fmt) => {
    if (!pack) return;
    try {
      const { data, headers } = await api.get(`/memory/handover/${pack.pack_id}/download`, {
        params: { fmt },
        responseType: "blob",
      });
      const dispo = headers["content-disposition"] || "";
      const m = dispo.match(/filename="([^"]+)"/);
      const fname = m ? m[1] : `handover.${fmt}`;
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Download failed");
    }
  };

  const toggleDeparture = async () => {
    if (!selectedPerson || togglingDeparture) return;
    setTogglingDeparture(true);
    try {
      const { data } = await api.post(`/memory/departure/${selectedId}`);
      toast.success(data.departing
        ? `Marked as departing — handover pack auto-generated`
        : "Departure cleared");
      loadPeople();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to toggle departure");
    } finally { setTogglingDeparture(false); }
  };

  const daysLeft = selectedPerson?.departure_date
    ? Math.max(0, Math.ceil((new Date(selectedPerson.departure_date).getTime() - Date.now()) / 86400000))
    : null;

  return (
    <div className="flex h-full min-h-[calc(100vh-4rem)]" data-testid="memory-page">
      {/* People sidebar */}
      <aside className="w-72 shrink-0 border-r border-border/60 flex flex-col">
        <div className="px-5 py-5 border-b border-border/60">
          <p className="eyebrow mb-2">Individual memory</p>
          <h1 className="font-display text-xl leading-tight">People</h1>
          <p className="text-xs text-muted-foreground mt-1">Continuity when teammates leave.</p>
        </div>
        <div className="flex-1 overflow-auto p-2">
          {loadingPeople ? (
            <div className="p-2 space-y-1.5" data-testid="memory-people-skeleton" role="status" aria-label="Loading people">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="px-3 py-2.5 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-muted animate-pulse shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 w-3/4 rounded bg-muted animate-pulse" />
                    <div className="h-2 w-1/2 rounded bg-muted/70 animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            people.map((p) => {
              const active = p.user_id === selectedId;
              return (
                <button
                  key={p.user_id}
                  onClick={() => setSelectedId(p.user_id)}
                  className={`w-full text-left px-3 py-2.5 rounded-md flex items-center gap-3 transition-colors ${
                    active ? "bg-secondary" : "hover:bg-secondary/50"
                  }`}
                  data-testid={`memory-person-${p.user_id}`}
                >
                  <div className="w-8 h-8 rounded-full bg-foreground text-background flex items-center justify-center text-xs font-semibold shrink-0">
                    {(p.name || p.email || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate flex items-center gap-2">
                      <span className="truncate">{p.name || p.email}</span>
                      {p.user_id === user?.id && (
                        <span className="text-[9px] uppercase tracking-wider text-muted-foreground">you</span>
                      )}
                      {p.departure_date && (
                        <span className="text-[9px] uppercase tracking-wider text-amber-500 border border-amber-500/40 rounded-full px-1.5 py-px" data-testid={`departing-badge-${p.user_id}`}>
                          departing
                        </span>
                      )}
                    </p>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      {p.role} · {p.memory_count} entr{p.memory_count === 1 ? "y" : "ies"}
                    </p>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </aside>

      {/* Main pane */}
      <section className="flex-1 min-w-0 flex flex-col">
        {selectedPerson ? (
          <>
            <header className="px-8 py-6 border-b border-border/60">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="eyebrow mb-2 flex items-center gap-2">
                    <User className="w-3.5 h-3.5" /> {selectedPerson.role}
                  </p>
                  <h2 className="font-display text-3xl">{selectedPerson.name || selectedPerson.email}</h2>
                  <p className="text-sm text-muted-foreground mt-1">{selectedPerson.email}</p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {(selectedPerson.aliases || []).slice(0, 6).map((a) => (
                      <span key={a} className="text-[10px] font-mono text-muted-foreground border border-border/60 rounded-full px-2 py-0.5 flex items-center gap-1">
                        <Hash className="w-2.5 h-2.5" /> {a}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Link to={`/app/chat?prefill=/${selectedPerson.aliases?.[0] || _slug(selectedPerson.name)} `}>
                    <Button variant="outline" className="rounded-full" data-testid="memory-ask-btn">
                      <MessageSquare className="w-4 h-4 mr-2" /> Ask AI about them
                    </Button>
                  </Link>
                  {canEditSubject && (
                    <>
                      <Button variant="outline" onClick={() => { setAliasStr((selectedPerson.aliases || []).join(" ")); setShowAlias(true); }} className="rounded-full" data-testid="memory-aliases-btn">
                        <TagIcon className="w-4 h-4 mr-2" /> Aliases
                      </Button>
                      <Button onClick={() => setShowAdd(true)} className="rounded-full" data-testid="memory-add-btn">
                        <Plus className="w-4 h-4 mr-2" /> Add entry
                      </Button>
                    </>
                  )}
                  {(isAdmin || isSelf) && (
                    <>
                      <Button
                        onClick={() => setShowHandoverPreview(true)}
                        variant="ghost"
                        className="rounded-full"
                        data-testid="handover-preview-btn"
                      >
                        <Eye className="w-4 h-4 mr-2" /> Preview handover
                      </Button>
                      <Button
                        onClick={generateHandover}
                        disabled={generating}
                        variant="outline"
                        className="rounded-full"
                        data-testid="handover-generate-btn"
                      >
                        {generating
                          ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Compiling {selectedPerson.name?.split(" ")[0]}'s institutional knowledge…</>
                          : <><FileDown className="w-4 h-4 mr-2" /> Generate Handover Pack</>}
                      </Button>
                    </>
                  )}
                  {isAdmin && (
                    <Button
                      onClick={toggleDeparture}
                      disabled={togglingDeparture}
                      variant={selectedPerson.departure_date ? "outline" : "ghost"}
                      className={`rounded-full ${selectedPerson.departure_date ? "border-amber-500/60 text-amber-500 hover:bg-amber-500/10" : "text-muted-foreground"}`}
                      data-testid="departure-toggle-btn"
                    >
                      <Plane className="w-4 h-4 mr-2" />
                      {togglingDeparture ? "Working…" : (selectedPerson.departure_date ? "Clear departure" : "Mark as departing")}
                    </Button>
                  )}
                </div>
              </div>

              {daysLeft !== null && (
                <div
                  className="mt-4 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex items-center gap-3"
                  data-testid="departure-countdown-banner"
                >
                  <CalendarClock className="w-4 h-4 text-amber-500 shrink-0" />
                  <p className="text-sm text-amber-600 dark:text-amber-400">
                    <span className="font-semibold">{daysLeft} day{daysLeft === 1 ? "" : "s"} remaining</span>
                    {" — handover pack "}
                    {selectedPerson.handover_last_generated
                      ? <>last updated {new Date(selectedPerson.handover_last_generated).toLocaleDateString()}</>
                      : <>not generated yet</>}
                  </p>
                </div>
              )}

              {pack && (
                <div className="mt-4 card-flat p-5" data-testid="handover-preview-card">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <p className="eyebrow mb-1.5">Handover pack ready</p>
                      <p className="font-display text-xl">{pack.payload.employee.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {pack.payload.counts.decisions} decisions · {pack.payload.counts.processes} processes · {pack.payload.counts.total} entries
                      </p>
                    </div>
                    <div className="text-right">
                      <CoverageBadge score={pack.payload.coverage_score} />
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1.5">coverage</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed line-clamp-4">{pack.payload.narrative}</p>
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" onClick={() => downloadPack("pdf")} className="rounded-full" data-testid="handover-download-pdf">
                      <FileDown className="w-3.5 h-3.5 mr-1.5" /> PDF
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => downloadPack("json")} className="rounded-full" data-testid="handover-download-json">
                      <FileJson className="w-3.5 h-3.5 mr-1.5" /> JSON
                    </Button>
                  </div>
                </div>
              )}
              <div className="mt-5 relative max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder={`Search ${selectedPerson.name?.split(" ")[0] || "their"} memory…`}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  data-testid="memory-search-input"
                />
              </div>
            </header>

            <div className="flex-1 overflow-auto p-8">
              {loadingEntries ? (
                <ul className="space-y-3" data-testid="memory-entries-skeleton" role="status" aria-label="Loading memory entries">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <li key={i} className="bento p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-8 h-8 rounded-md bg-muted animate-pulse shrink-0" />
                        <div className="flex-1 space-y-3">
                          <div className="flex items-center gap-2">
                            <div className="h-4 w-1/3 rounded bg-muted animate-pulse" />
                            <div className="h-4 w-16 rounded-full bg-muted/70 animate-pulse" />
                          </div>
                          <div className="h-3 w-full rounded bg-muted/60 animate-pulse" />
                          <div className="h-3 w-5/6 rounded bg-muted/60 animate-pulse" />
                          <div className="h-3 w-2/3 rounded bg-muted/60 animate-pulse" />
                          <div className="h-2.5 w-40 rounded bg-muted/70 animate-pulse" />
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : entries.length === 0 ? (
                <div className="card-flat p-10 text-center max-w-lg mx-auto flex flex-col items-center gap-3" data-testid="memory-empty">
                  <div className="empty-icon w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-500">
                    <Notebook className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <p className="font-display text-lg">
                      {isSelf ? "Nothing captured yet" : `${selectedPerson.name?.split(" ")[0] || "They"} hasn't captured anything yet`}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      As {isSelf ? "you" : selectedPerson.name?.split(" ")[0] || "they"} work in RAGNES (edit knowledge, upload sources, ask AI), entries land here automatically.
                      {canEditSubject && " Or add a note manually to seed the memory."}
                    </p>
                  </div>
                  {canEditSubject && (
                    <Button
                      size="sm"
                      onClick={() => setShowAdd(true)}
                      className="rounded-full mt-2"
                      data-testid="memory-empty-add-btn"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1.5" /> Add first entry
                    </Button>
                  )}
                </div>
              ) : (
                <ul className="space-y-3">
                  {entries.map((e) => {
                    const meta = KIND_META[e.kind] || KIND_META.activity;
                    const Icon = meta.icon;
                    return (
                      <li key={e.id} className="bento p-5" data-testid={`memory-entry-${e.id}`}>
                        <div className="flex items-start gap-4">
                          <div className={`w-8 h-8 rounded-md border border-border/60 flex items-center justify-center shrink-0 ${meta.color}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium">{e.title}</p>
                              <span className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground border border-border/60 rounded-full px-2 py-0.5">
                                {meta.label}
                              </span>
                              {e.private && (
                                <span className="text-[10px] uppercase tracking-wider font-mono text-amber-400 border border-amber-500/40 rounded-full px-2 py-0.5 flex items-center gap-1">
                                  <Lock className="w-2.5 h-2.5" /> private
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1.5 whitespace-pre-wrap break-words line-clamp-6">
                              {e.content}
                            </p>
                            {e.tags?.length > 0 && (
                              <div className="mt-3 flex flex-wrap gap-1.5">
                                {e.tags.map((t) => (
                                  <span key={t} className="text-[10px] font-mono text-muted-foreground border border-border/60 rounded-full px-2 py-0.5">{t}</span>
                                ))}
                              </div>
                            )}
                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-3 font-mono">
                              {timeAgo(e.created_at)} · {new Date(e.created_at).toLocaleString()}
                              {e.source_ref?.type && ` · via ${e.source_ref.type}`}
                            </p>
                          </div>
                          {canEditSubject && (
                            <div className="flex flex-col gap-1 shrink-0">
                              <button onClick={() => togglePrivate(e)} className="text-muted-foreground hover:text-foreground p-1" title={e.private ? "Make visible" : "Make private"} data-testid={`memory-toggle-private-${e.id}`}>
                                {e.private ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                              </button>
                              <button onClick={() => removeEntry(e.id)} className="text-muted-foreground hover:text-destructive p-1" title="Delete" data-testid={`memory-delete-${e.id}`}>
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </>
        ) : (
          <div className="p-8 text-muted-foreground text-sm">Pick a person from the list.</div>
        )}
      </section>

      {/* Add entry dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add memory entry
            </DialogTitle>
            <DialogDescription>
              {isSelf
                ? "Capture a decision, process, or reflection so future you (or your team) can find it later."
                : `Adding to ${selectedPerson?.name}'s memory (admin write-through).`}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-4 gap-2">
              {["note", "decision", "process", "activity"].map((k) => (
                <button
                  key={k}
                  onClick={() => setNote({ ...note, kind: k })}
                  className={`text-xs uppercase tracking-wider py-2 rounded-md border transition-colors ${
                    note.kind === k
                      ? "border-foreground bg-secondary text-foreground"
                      : "border-border/60 text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid={`memory-kind-${k}`}
                >
                  {k}
                </button>
              ))}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mem-title">Title</Label>
              <Input id="mem-title" value={note.title} onChange={(e) => setNote({ ...note, title: e.target.value })} data-testid="memory-title-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mem-content">Content</Label>
              <Textarea id="mem-content" rows={6} value={note.content} onChange={(e) => setNote({ ...note, content: e.target.value })} data-testid="memory-content-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mem-tags">Tags (comma-separated)</Label>
              <Input id="mem-tags" value={note.tags} onChange={(e) => setNote({ ...note, tags: e.target.value })} placeholder="q1, pricing, onboarding" data-testid="memory-tags-input" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={note.private} onChange={(e) => setNote({ ...note, private: e.target.checked })} data-testid="memory-private-checkbox" />
              <span>Private — only I and admins can view</span>
            </label>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="ghost" onClick={() => setShowAdd(false)} className="rounded-full">Cancel</Button>
              <Button onClick={submitNote} disabled={saving} className="rounded-full" data-testid="memory-submit-btn">
                {saving ? "Saving…" : "Save entry"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Aliases dialog */}
      <Dialog open={showAlias} onOpenChange={setShowAlias}>        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <TagIcon className="w-4 h-4" /> Mention aliases
            </DialogTitle>
            <DialogDescription>
              Add short aliases so teammates can `/mention` this person in chat. Separate with spaces or commas.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-3">
            <Input
              value={aliasStr}
              onChange={(e) => setAliasStr(e.target.value)}
              placeholder="marc mm marc-m"
              autoFocus
              data-testid="memory-aliases-input"
            />
            <p className="text-[11px] text-muted-foreground">
              Name and email-local slugs are always recognised implicitly.
            </p>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="ghost" onClick={() => setShowAlias(false)} className="rounded-full">Cancel</Button>
              <Button onClick={saveAliases} className="rounded-full" data-testid="memory-aliases-save-btn">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Handover preview drawer — peek before generating the full pack */}
      <HandoverPreviewDrawer
        subjectUserId={selectedId}
        subjectName={selectedPerson?.name?.split(" ")[0] || "Them"}
        isOpen={showHandoverPreview}
        onClose={() => setShowHandoverPreview(false)}
        onGenerateFull={generateHandover}
        generating={generating}
      />
    </div>
  );
}

// Colour-coded coverage badge: green ≥70%, amber 40–69%, red <40%.
function CoverageBadge({ score }) {
  const tone =
    score >= 70
      ? "text-emerald-500 border-emerald-500/40 bg-emerald-500/10"
      : score >= 40
        ? "text-amber-500 border-amber-500/40 bg-amber-500/10"
        : "text-red-500 border-red-500/40 bg-red-500/10";
  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 font-display text-lg ${tone}`} data-testid="coverage-badge">
      {score}%
    </span>
  );
}

// Local slug so the "Ask AI about them" link prefills correctly even before aliases are set.
function _slug(s) { return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""); }
